import React, { useEffect, useRef, useState } from "react";
import ModalB from "react-bootstrap/Modal";
import { Html5Qrcode } from "html5-qrcode";
import { FaCheck } from "react-icons/fa";
import api from "../../services/axios";
import { MAX_VAGAS_POR_CONTEUDO } from "../credenciamentoConteudos/conteudosAgendaUtils";
import "./modalCheckinQr.css";

const SCANNER_ELEMENT_ID = "checkin-qr-conteudo-reader";

function encontrarParticipante(participantes, idUsuario) {
  if (!Array.isArray(participantes) || !Number.isFinite(idUsuario)) {
    return null;
  }

  return (
    participantes.find(
      (p) => Number(p.id_usuario ?? p.usuario?.id) === Number(idUsuario)
    ) || null
  );
}

function temSaidaAgenda(participante) {
  return Boolean(participante?.saida_agenda || participante?.saidaAgenda);
}

function formatarHorarioSql(valor) {
  if (!valor) return "";
  const texto = String(valor);
  if (texto.length >= 16) {
    return texto.substring(11, 16);
  }
  return texto;
}

function labelTipoAgenda(tipo) {
  if (tipo === "reuniao_negocio") return "Reunião de negócio";
  if (tipo === "conteudo_patrocinado") return "Conteúdo patrocinado";
  if (tipo === "conteudo_editorial") return "Conteúdo editorial";
  if (tipo === "esporte") return "Esporte";
  return tipo || "Agenda";
}

function liberarTrilhasCamera() {
  const elemento = document.getElementById(SCANNER_ELEMENT_ID);
  if (!elemento) return;

  const videos = elemento.querySelectorAll("video");
  videos.forEach((video) => {
    const stream = video.srcObject;
    if (stream && typeof stream.getTracks === "function") {
      stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (_) {
          // ignora
        }
      });
    }
    video.srcObject = null;
    try {
      video.pause();
    } catch (_) {
      // ignora
    }
  });
}

function ModalCheckinQrConteudo(props) {
  const [tituloModal, setTituloModal] = useState(
    props.titulo || "Check-in por QR"
  );
  const [mensagemErro, setMensagemErro] = useState("");
  const [usuarioVerificado, setUsuarioVerificado] = useState(false);
  const [processando, setProcessando] = useState(false);
  const [cameraErro, setCameraErro] = useState("");
  const [idLidoQr, setIdLidoQr] = useState(null);
  const [participanteLido, setParticipanteLido] = useState(null);
  const [nomeConfirmado, setNomeConfirmado] = useState("");
  const [aguardandoConfirmacao, setAguardandoConfirmacao] = useState(false);
  const [qrCorresponde, setQrCorresponde] = useState(null);
  const [modoInclusao, setModoInclusao] = useState(false);
  const [incluidoManual, setIncluidoManual] = useState(false);
  const [modoSaida, setModoSaida] = useState(false);
  const [saidaRegistrada, setSaidaRegistrada] = useState(false);
  const [conflitoAgenda, setConflitoAgenda] = useState(null);
  const [carregandoDadosConvidado, setCarregandoDadosConvidado] =
    useState(false);

  const scannerRef = useRef(null);
  const processandoRef = useRef(false);
  const verificadoRef = useRef(false);
  const aguardandoConfirmacaoRef = useRef(false);
  const modalAbertoRef = useRef(false);
  const inicioIdRef = useRef(0);

  const nomeParticipante =
    participanteLido?.usuario?.nome || nomeConfirmado || "convidado";
  const confirmacaoBloqueada = processando || carregandoDadosConvidado;

  const totalParticipantes = Array.isArray(props.item?.participantes)
    ? props.item.participantes.length
    : 0;
  const ehEsporte = props.item?.tipo === "esporte";
  // Conteúdo: limite de 45 vagas. Esporte: sem limite de entrada.
  const temVaga = ehEsporte || totalParticipantes < MAX_VAGAS_POR_CONTEUDO;

  const pararScanner = async () => {
    inicioIdRef.current += 1;
    const scanner = scannerRef.current;
    scannerRef.current = null;

    if (scanner) {
      try {
        const estado = scanner.getState?.();
        if (estado === 2 || estado === 3) {
          await scanner.stop();
        }
      } catch (_) {
        // ignora erros ao parar
      }

      try {
        scanner.clear();
      } catch (_) {
        // ignora
      }
    }

    liberarTrilhasCamera();
  };

  const iniciarScanner = async () => {
    if (
      !modalAbertoRef.current ||
      verificadoRef.current ||
      aguardandoConfirmacaoRef.current
    ) {
      return;
    }

    await pararScanner();
    const inicioId = ++inicioIdRef.current;

    if (
      !modalAbertoRef.current ||
      verificadoRef.current ||
      aguardandoConfirmacaoRef.current ||
      inicioId !== inicioIdRef.current
    ) {
      return;
    }

    setCameraErro("");

    const elemento = document.getElementById(SCANNER_ELEMENT_ID);
    if (!elemento) return;

    try {
      const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: (viewfinderWidth, viewfinderHeight) => {
            const size = Math.min(viewfinderWidth, viewfinderHeight, 240);
            return { width: size, height: size };
          },
          aspectRatio: 1,
          disableFlip: false,
        },
        (decodedText) => {
          handleLeituraQr(decodedText);
        },
        () => {
          // frame sem QR — ignorar
        }
      );

      if (
        !modalAbertoRef.current ||
        verificadoRef.current ||
        aguardandoConfirmacaoRef.current ||
        inicioId !== inicioIdRef.current
      ) {
        await pararScanner();
      }
    } catch (err) {
      if (inicioId !== inicioIdRef.current || !modalAbertoRef.current) {
        liberarTrilhasCamera();
        return;
      }
      console.error("Erro ao iniciar câmera:", err);
      setCameraErro(
        "Não foi possível acessar a câmera. Verifique as permissões do navegador."
      );
    }
  };

  const buscarNomeUsuario = async (idUsuario) => {
    try {
      const resp = await api.get(`/usuarios-get/${idUsuario}`);
      return resp.data?.user?.nome || "";
    } catch (_) {
      return "";
    }
  };

  const handleLeituraQr = async (textoQr) => {
    if (
      processandoRef.current ||
      verificadoRef.current ||
      aguardandoConfirmacaoRef.current
    ) {
      return;
    }

    const idLido = Number(String(textoQr).trim());
    if (!Number.isFinite(idLido) || idLido <= 0) {
      setMensagemErro("QR Code inválido. Use o crachá do convidado.");
      return;
    }

    const participante = encontrarParticipante(
      props.item?.participantes,
      idLido
    );

    processandoRef.current = true;
    aguardandoConfirmacaoRef.current = true;
    await pararScanner();

    setIdLidoQr(idLido);
    setParticipanteLido(participante);
    setNomeConfirmado("");
    setAguardandoConfirmacao(true);
    setModoInclusao(false);
    setIncluidoManual(false);
    setModoSaida(false);
    setConflitoAgenda(null);
    setCarregandoDadosConvidado(!participante?.usuario?.nome);

    if (!participante) {
      const nome = await buscarNomeUsuario(idLido);
      if (nome) {
        setNomeConfirmado(nome);
      }

      if (temVaga) {
        setModoInclusao(true);
        setQrCorresponde(true);
        setMensagemErro("");
      } else {
        setQrCorresponde(false);
        setMensagemErro(
          "O convidado não está na listagem de participantes e não há vagas disponíveis."
        );
      }
    } else if (participante.presenca) {
      if (temSaidaAgenda(participante)) {
        // Já saiu: nova leitura = reentrada (check-in novamente)
        setQrCorresponde(true);
        setMensagemErro("");
      } else {
        setModoSaida(true);
        setQrCorresponde(true);
        setMensagemErro("");
      }
    } else {
      setQrCorresponde(true);
      setMensagemErro("");
    }

    setCarregandoDadosConvidado(false);
    processandoRef.current = false;
  };

  const cancelarConfirmacao = () => {
    aguardandoConfirmacaoRef.current = false;
    setAguardandoConfirmacao(false);
    setIdLidoQr(null);
    setParticipanteLido(null);
    setNomeConfirmado("");
    setQrCorresponde(null);
    setModoInclusao(false);
    setModoSaida(false);
    setConflitoAgenda(null);
    setCarregandoDadosConvidado(false);
    setMensagemErro("");
    setProcessando(false);
    processandoRef.current = false;
    requestAnimationFrame(() => {
      setTimeout(() => {
        iniciarScanner();
      }, 80);
    });
  };

  const confirmarCheckin = async () => {
    if (
      !idLidoQr ||
      processandoRef.current ||
      verificadoRef.current ||
      carregandoDadosConvidado
    ) {
      return;
    }

    if (!participanteLido && !modoInclusao) {
      setQrCorresponde(false);
      setMensagemErro(
        "O convidado não está na listagem de participantes."
      );
      return;
    }

    processandoRef.current = true;
    setProcessando(true);
    setMensagemErro("");
    setConflitoAgenda(null);

    try {
      const resp = await api.post("/reunioes-valida-conteudo-qr", {
        id_usuario_qr: Number(idLidoQr),
        id_agenda: props.item.id,
        incluir_se_ausente: Boolean(modoInclusao),
      });

      const sucesso =
        resp.data.reuniao &&
        (resp.data.mensagem === "Presença confirmada!" ||
          resp.data.mensagem === "Convidado incluído e presença confirmada!" ||
          resp.data.mensagem === "Saída registrada!");

      if (sucesso) {
        verificadoRef.current = true;
        aguardandoConfirmacaoRef.current = false;
        setAguardandoConfirmacao(false);
        setUsuarioVerificado(true);
        setIncluidoManual(Boolean(resp.data.incluido));
        setSaidaRegistrada(Boolean(resp.data.saida));
        setNomeConfirmado(resp.data.usuario?.nome || nomeParticipante);
        setMensagemErro("");
        setConflitoAgenda(null);
        props.onCheckinConfirmado?.();
      } else if (resp.data.conflito) {
        setConflitoAgenda(resp.data.conflito);
        setMensagemErro(
          resp.data.mensagem ||
            "O convidado possui outro compromisso neste horário."
        );
        setQrCorresponde(false);
        setModoInclusao(false);
        processandoRef.current = false;
        setProcessando(false);
      } else {
        setMensagemErro(
          resp.data.mensagem || "Não foi possível confirmar o check-in."
        );
        setQrCorresponde(false);
        setModoInclusao(Boolean(resp.data.pode_incluir));
        processandoRef.current = false;
        setProcessando(false);
      }
    } catch (err) {
      setMensagemErro(
        err?.response?.data?.mensagem || "Erro ao validar o QR Code."
      );
      setQrCorresponde(false);
      setConflitoAgenda(err?.response?.data?.conflito || null);
      processandoRef.current = false;
      setProcessando(false);
    }
  };

  useEffect(() => {
    setTituloModal(props.titulo || "Check-in por QR");
  }, [props.titulo]);

  useEffect(() => {
    modalAbertoRef.current = Boolean(props.show);

    if (props.show) {
      return () => {
        modalAbertoRef.current = false;
        pararScanner();
      };
    }

    pararScanner();
    setMensagemErro("");
    setUsuarioVerificado(false);
    setProcessando(false);
    setCameraErro("");
    setIdLidoQr(null);
    setParticipanteLido(null);
    setNomeConfirmado("");
    setQrCorresponde(null);
    setModoInclusao(false);
    setIncluidoManual(false);
    setModoSaida(false);
    setSaidaRegistrada(false);
    setConflitoAgenda(null);
    setCarregandoDadosConvidado(false);
    setAguardandoConfirmacao(false);
    processandoRef.current = false;
    verificadoRef.current = false;
    aguardandoConfirmacaoRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show]);

  const handleModal = async () => {
    modalAbertoRef.current = false;
    await pararScanner();
    props.toggleModal();
  };

  const renderCorpo = () => {
    if (usuarioVerificado) {
      return (
        <>
          <div className="checkin-qr-sucesso">
            <FaCheck />
            <div>
              {saidaRegistrada
                ? "Saída registrada!"
                : incluidoManual
                  ? "Incluído e presença confirmada!"
                  : "Presença Confirmada!"}
            </div>
            <div className="checkin-qr-sucesso-nome">{nomeParticipante}</div>
          </div>
          <div className="area-botao-modal">
            <button className="btn-header-options" onClick={handleModal}>
              Fechar
            </button>
          </div>
        </>
      );
    }

    if (aguardandoConfirmacao) {
      return (
        <div className="checkin-qr-confirmacao">
          {qrCorresponde === false ? (
            <>
              {conflitoAgenda ? (
                <div className="checkin-qr-conflito">
                  <p className="checkin-qr-confirmacao-titulo">
                    Convidado com outro compromisso
                  </p>
                  <span className="checkin-qr-confirmacao-nome">
                    {nomeParticipante}
                  </span>
                  <div className="checkin-qr-conflito-card">
                    <div className="checkin-qr-conflito-tipo">
                      {labelTipoAgenda(conflitoAgenda.tipo)}
                    </div>
                    <div className="checkin-qr-conflito-nome">
                      {conflitoAgenda.nome}
                    </div>
                    {conflitoAgenda.com_quem ? (
                      <div className="checkin-qr-conflito-meta">
                        Com: <strong>{conflitoAgenda.com_quem}</strong>
                      </div>
                    ) : null}
                    <div className="checkin-qr-conflito-meta">
                      Horário:{" "}
                      <strong>
                        {formatarHorarioSql(conflitoAgenda.dt_hora_inicio)}
                        {conflitoAgenda.dt_hora_fim
                          ? ` / ${formatarHorarioSql(conflitoAgenda.dt_hora_fim)}`
                          : ""}
                      </strong>
                    </div>
                    {conflitoAgenda.local ? (
                      <div className="checkin-qr-conflito-meta">
                        Local: <strong>{conflitoAgenda.local}</strong>
                      </div>
                    ) : null}
                    <div className="checkin-qr-conflito-destino">
                      {conflitoAgenda.destino}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="checkin-qr-erro checkin-qr-erro--destaque">
                  {mensagemErro ||
                    "O convidado não está na listagem de participantes."}
                </p>
              )}
              <div className="checkin-qr-confirmacao-acoes">
                <button
                  type="button"
                  className="checkin-qr-btn checkin-qr-btn--primario"
                  onClick={cancelarConfirmacao}
                  disabled={processando}
                >
                  Ler novamente
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="checkin-qr-confirmacao-titulo">
                {modoInclusao
                  ? "Incluir e confirmar check-in?"
                  : modoSaida
                    ? "Confirmar saída?"
                    : "Confirmar check-in?"}
              </p>
              <p
                className={`checkin-qr-confirmacao-texto${
                  modoSaida ? " checkin-qr-confirmacao-texto--saida" : ""
                }`}
              >
                {carregandoDadosConvidado ? (
                  <span className="checkin-qr-confirmacao-detalhe">
                    Carregando dados do convidado…
                  </span>
                ) : modoInclusao ? (
                  <>
                    <span className="checkin-qr-confirmacao-nome">
                      {nomeParticipante}
                    </span>
                    <span className="checkin-qr-confirmacao-detalhe">
                      não está na listagem de participantes.
                      <br />
                      Há vaga disponível. Deseja incluir e confirmar a presença
                      neste conteúdo?
                    </span>
                  </>
                ) : (
                  <>
                    <span className="checkin-qr-confirmacao-detalhe">
                      QR Code lido para
                    </span>
                    <span className="checkin-qr-confirmacao-nome">
                      {nomeParticipante}
                    </span>
                    <span className="checkin-qr-confirmacao-detalhe">
                      {modoSaida
                        ? "Presença já confirmada. Deseja registrar a saída deste conteúdo?"
                        : temSaidaAgenda(participanteLido)
                          ? "Saída já registrada. Deseja confirmar nova entrada neste conteúdo?"
                          : "Deseja confirmar a presença neste conteúdo?"}
                    </span>
                  </>
                )}
              </p>

              {mensagemErro ? (
                <div className="checkin-qr-erro checkin-qr-erro--destaque">
                  {mensagemErro}
                </div>
              ) : null}

              <div className="checkin-qr-confirmacao-acoes">
                <button
                  type="button"
                  className="checkin-qr-btn checkin-qr-btn--secundario"
                  onClick={cancelarConfirmacao}
                  disabled={processando}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="checkin-qr-btn checkin-qr-btn--primario"
                  onClick={confirmarCheckin}
                  disabled={confirmacaoBloqueada}
                >
                  {carregandoDadosConvidado
                    ? "Carregando…"
                    : processando
                      ? "Confirmando…"
                      : modoInclusao
                        ? "Incluir e confirmar"
                        : modoSaida
                          ? "Confirmar saída"
                          : "Confirmar check-in"}
                </button>
              </div>
            </>
          )}
        </div>
      );
    }

    return (
      <>
        <p className="checkin-qr-instrucao">
          Aponte a câmera para o QR Code do crachá do convidado
        </p>

        {cameraErro ? (
          <div className="checkin-qr-erro">{cameraErro}</div>
        ) : null}

        {mensagemErro ? (
          <div className="checkin-qr-erro">{mensagemErro}</div>
        ) : null}
      </>
    );
  };

  return (
    <ModalB
      show={props.show}
      onHide={handleModal}
      onEnter={() => {
        modalAbertoRef.current = true;
      }}
      onEntered={() => {
        if (
          modalAbertoRef.current &&
          !aguardandoConfirmacaoRef.current &&
          !verificadoRef.current
        ) {
          iniciarScanner();
        }
      }}
      onExit={() => {
        modalAbertoRef.current = false;
        pararScanner();
      }}
      onExited={() => {
        modalAbertoRef.current = false;
        pararScanner();
      }}
      backdrop="static"
      centered
      dialogClassName="modal-checkin-qr-dialog"
    >
      <ModalB.Header closeButton>
        <ModalB.Title className="titulo-modal">{tituloModal}</ModalB.Title>
      </ModalB.Header>
      <ModalB.Body>
        <div className="area-modal-validacao modal-checkin-qr">
          <div className="checkin-qr-cabecalho-conteudo">
            <div className="label-agenda-modal checkin-qr-nome-convidado">
              {props.item?.nome || ""}
            </div>
          </div>

          <div className="area-input-modal">
            <div
              id={SCANNER_ELEMENT_ID}
              className={`checkin-qr-reader${
                aguardandoConfirmacao || usuarioVerificado
                  ? " checkin-qr-reader--oculto"
                  : ""
              }`}
            />
            {renderCorpo()}
          </div>
        </div>
      </ModalB.Body>
    </ModalB>
  );
}

export default ModalCheckinQrConteudo;
